import moment from "moment";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
// Imported by module path, not through ../utils, to keep the store out of that
// barrel's dependency graph.
import { readUrlSelection, writeUrlSelection } from "../utils/handle-url-param";

// --- Types ---
export type IState = {
  updatedAt: number;
  user: {
    id: string;
    name: string;
  };
  location: string;
  version: {
    id: string;
    title: string;
  };
  story: {
    id: string;
    title: string;
  };
  folder: {
    id: string;
    name: string;
  };
  date: {
    dateFrom: string;
    dateTo: string;
    index: number;
  };
  showUnread: boolean;
  showMobileMenu: boolean;
  /**
   * The page was opened from a deep link that named a story and/or version
   * (HubSpot's "Go to analytics" / "View analytics"). Such a link is asking for
   * one specific version's real numbers, so it also opts the page out of
   * placeholder/demo data — see use-analytics-query.
   *
   * Seeded from the URL and never flipped by a filter change, so switching
   * filters afterwards keeps showing real data instead of snapping back to the
   * canned set.
   */
  isDeepLink: boolean;
};

type IActions = {
  setUser: (user: { id: string; name: string }) => void;
  setLocation: (location: string) => void;
  setVersion: (version: { id: string; title: string }) => void;
  setStory: (story: { id: string; title: string }) => void;
  setFolder: (folder: { id: string; name: string }) => void;
  setShowUnread: (showUnread: boolean) => void;
  setShowMobileMenu: (showMobileMenu: boolean) => void;
  setDate: (date: { dateFrom: string; dateTo: string; index: number }) => void;
  setUpdatedAt: (updatedAt: number) => void;
  setMultipleFilters: (data: Partial<IState>) => void;
  /** Re-derives the story/version selection from the URL. See the action. */
  adoptUrlSelection: () => void;
  reset: () => void;
};

type Store = IState & IActions;

// --- Helpers ---
const getInitialDate = (): IState["date"] => ({
  dateFrom: moment().subtract(29, "days").format("YYYY-MM-DD"),
  dateTo: moment().add(1, "days").format("YYYY-MM-DD"),
  index: 3,
});

// The story/version filters are derived from the URL rather than copied out of
// it, so they survive a refresh, a back navigation and the reset() that runs on
// unmount. Titles start empty and are filled in by use-deep-link-selection.
const getInitialState = (): IState => {
  const { storyId, versionId } = readUrlSelection();
  return {
    updatedAt: moment().valueOf(),
    user: { id: "", name: "" },
    version: { id: versionId, title: "" },
    story: { id: storyId, title: "" },
    folder: { id: "", name: "" },
    location: "",
    date: getInitialDate(),
    showUnread: true,
    showMobileMenu: false,
    isDeepLink: Boolean(storyId || versionId),
  };
};

// --- Store ---
export const useAnalyticsStore = create<Store>((set, get) => {
  const initialState = getInitialState();

  return {
    ...initialState,
    setUser: (user) => set((state) => ({ ...state, user })),
    setLocation: (location) => set((state) => ({ ...state, location })),
    // setVersion/setStory/setMultipleFilters mirror the selection into the query
    // string so the URL and the store can never disagree: reset() re-derives
    // both filters from the URL, so a selection that only lived in the store
    // would silently vanish on the next remount.
    setVersion: (version) => {
      writeUrlSelection({ versionId: version.id });
      set((state) => ({ ...state, version }));
    },
    setStory: (story) => {
      writeUrlSelection({ storyId: story.id });
      set((state) => ({ ...state, story }));
    },
    setFolder: (folder) => set((state) => ({ ...state, folder })),
    setDate: (date) => set((state) => ({ ...state, date })),
    setUpdatedAt: (updatedAt) => set((state) => ({ ...state, updatedAt })),
    setShowUnread: (showUnread) => set((state) => ({ ...state, showUnread })),
    setShowMobileMenu: (showMobileMenu) =>
      set((state) => ({ ...state, showMobileMenu })),
    setMultipleFilters: (data) => {
      if ("story" in data || "version" in data) {
        writeUrlSelection({
          ...("story" in data && { storyId: data.story?.id ?? "" }),
          ...("version" in data && { versionId: data.version?.id ?? "" }),
        });
      }
      set((state) => ({ ...state, ...data }));
    },
    /**
     * Re-derives the selection from the URL.
     *
     * The initial state is computed when this module is evaluated, which on a
     * full load of /pages/analytics is after the query string exists — but on a
     * click through from the Stories list the module was evaluated long before,
     * on whatever page the app started on, with no params to read. So the deep
     * link arrived in the URL and the filters ignored it, and only a refresh
     * appeared to fix it.
     *
     * Called on mount, so it must be idempotent: it writes nothing when the URL
     * carries no selection, or when the store already holds the same one.
     */
    adoptUrlSelection: () => {
      const { storyId, versionId } = readUrlSelection();
      if (!storyId && !versionId) return;

      const current = get();
      if (current.story.id === storyId && current.version.id === versionId) {
        return;
      }

      set((state) => ({
        ...state,
        // Titles are left empty on purpose: use-deep-link-selection fills them
        // from Mongo, and a stale title here would be shown as the pill's label.
        story: { id: storyId, title: "" },
        version: { id: versionId, title: "" },
        isDeepLink: true,
      }));
    },

    reset: () => set(getInitialState()),
  };
});

// --- Shallow Selector Hook ---
export const useAnalyticsStoreShallow = <U>(
  selector: (store: Store) => U,
): U => {
  return useAnalyticsStore(useShallow(selector));
};
